import { EventBroker } from "@ponder-core/events/eventBroker";
import { FilteredPreviewItem } from "@ponder-shared/tools/filter/filtered-preview-item";
import { PreviewItemFilters } from "@ponder-shared/tools/filter/preview-item-filters";
import { MagicCardFilterColor } from "./magic-card-filter-color";
import { take } from "rxjs";
import { FilteredPreviewItems } from "@ponder-shared/tools/filter/filtered-preview-items";
import { PreviewItemFilterOption } from "@ponder-shared/tools/filter/preview-item-filter-option";
import { MagicCardFilterRarity } from "./magic-card-filter-rarity";
import { MagicCardFilterName } from "./magic-card-filter-name";
import { MagicCardFilterText } from "./magic-card-filter-text";
import { MagicCardFilterOwnership } from "./magic-card-filter-ownership";
import { MagicCardFilterType } from "./magic-card-filter-type";
import { MagicCardSort } from "./magic-card-sort";
import { MagicCardFilterMana } from "./magic-card-filter-mana";
import { clearBit, setBit } from "@ponder-shared/utils/bitOperations";

export class MagicCardFilters extends PreviewItemFilters {

    private filterName = new MagicCardFilterName();
    private filterColor = new MagicCardFilterColor();
    private filterRarity = new MagicCardFilterRarity();
    private filterType = new MagicCardFilterType();
    private filterMana = new MagicCardFilterMana();
    private filterOwnership = new MagicCardFilterOwnership();
    private filterText = new MagicCardFilterText();
    private filterSort = new MagicCardSort();
    // private filterArtist = new MagicCardFilterArtist();

    // Separate ordering for how they should be displayed in the UI vs which order they should be run. For performance, we want to run the lighter-weight simpler filters first.
    private filtersDisplayOrder: PreviewItemFilterOption[] = [ this.filterName, this.filterSort, this.filterColor, this.filterRarity, this.filterType, this.filterMana, this.filterOwnership, this.filterText ];
    private filtersRunOrder: PreviewItemFilterOption[] = [ this.filterRarity, this.filterOwnership, this.filterColor, this.filterMana, this.filterType, this.filterName, this.filterText, this.filterSort ];
    
    public constructor(events: EventBroker) {
        super(events);
        this.events.ui.previewItemsFetchedEvent
            .pipe(take(1))
            .subscribe({
                next: (previewItems: FilteredPreviewItems) => {
                    this.prepare(previewItems.items);
                }
            });
    }

    public override getFilterOptions(): PreviewItemFilterOption[] {
        return this.filtersDisplayOrder;
    }

    public prepare(previewItems: FilteredPreviewItem[]): void {
        for (let i = 0; i < this.filtersRunOrder.length; ++i) {
            this.filtersRunOrder[i].prepare();
        }

        previewItems.forEach((item: FilteredPreviewItem) => {
            var magicCard = item.previewItem.item.data?.magic;

            if (magicCard) {
                for (let i = 0; i < this.filtersRunOrder.length; ++i) {
                    this.filtersRunOrder[i].track(magicCard, item.previewItem.collected);
                }
            }
        });

        this.isReady = true;
        this.events.ui.previewItemFilterReadyEvent.emit();
    }
    
    public isVisible(item: FilteredPreviewItem): boolean {
        var magicCard = item.previewItem.item.data?.magic;
        var collectedInfo = item.previewItem.collected;

        if (!magicCard) {
            return false;
        }

        // Check this item against each dirty filter and set the corresponding bit in its mask.
        for (let i = 0; i < this.filtersRunOrder.length; ++i) {
            if (this.filtersRunOrder[i].wasUpdated || (item.isVisible == undefined)) {
                var visible = this.filtersRunOrder[i].isValid(magicCard, collectedInfo);
                item.mask = visible ? setBit(item.mask, i) : clearBit(item.mask, i);
            }
        }
        
        // Return true if it has passed all filters
        return item.mask == FilterMaskFlag.All;
    }

    public override postVisibleCheck(): void {
        this.filtersRunOrder.forEach(f => { f.wasUpdated = false; } );
    }

    public sort(previewItems: FilteredPreviewItem[]): void {
        if (!this.filtersRunOrder) {
            return;
        }

        for (let i = 0; i < this.filtersRunOrder.length; ++i) {
            this.filtersRunOrder[i].sort(previewItems);
        }
    }

    public override setUserInfo(isCollection: boolean, isAuthed: boolean): void {
        this.filtersRunOrder.forEach(f => {
            f.setUserInfo(isCollection, isAuthed);
        });
    }
}

enum FilterMaskFlag {
    None      = 0b00000000,
    Name      = 0b00000001,
    Color     = 0b00000010,
    Rarity    = 0b00000100,
    Type      = 0b00001000,
    Mana      = 0b00010000,
    Ownership = 0b00100000,
    Text      = 0b01000000,
    Sort      = 0b10000000,
    All       = 0b11111111
}